import React from "react";
import { ArrowUpRight, ShieldCheck, Globe, Activity, HardHat, Zap } from "lucide-react";
import FIT from "../../Images/FIT.png";
import FNI from "../../Images/FNI.png";

const IndustrialHero = ({
  title = "Focus NDT Group",
  subtitle =
    "Focus NDT Group is a global industrial inspection and Non-Destructive Testing (NDT) organization providing PAUT, TOFD, UT, RT, MT, PT, welding inspection, shutdown support, and digital inspection solutions for oil & gas, offshore, shipyard, refinery, fabrication, and heavy engineering industries.",
  bgImage =
    "https://images.unsplash.com/photo-1516937941344-00b4e0337589?q=80&w=2000&auto=format&fit=crop",
  tagline = "Global Industrial Inspection & NDT Solutions",
  primaryBtnText = "Explore Group",
  secondaryBtnText = "Contact Us",
}) => {
  return (
    <section
      className="relative min-h-[95vh] flex items-center overflow-hidden bg-slate-950"
      aria-label="Focus NDT Group Industrial Inspection Services"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={bgImage}
          alt="Industrial NDT inspection offshore oil gas welding inspection"
          loading="eager"
          className="w-full h-full object-cover scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 text-white w-full py-20">
        <div className="max-w-5xl">

          {/* TAGLINE */}

          <div className="flex items-center space-x-3 mb-8">
            <div className="h-[2px] w-12 bg-orange-600"></div>

            <span className="text-[11px] font-black tracking-[0.4em] uppercase text-orange-500">
              {tagline}
            </span>
          </div>


          {/* H1 — VERY IMPORTANT FOR SEO */}

          <h1 className="text-5xl sm:text-6xl md:text-[90px] font-black tracking-tight leading-[0.9] mb-8">
            Focus NDT
            <br />
            <span className="text-orange-600">Group</span>
          </h1>


          {/* SEO TEXT */}

    {/* SEO TEXT */}

<div className="max-w-2xl border-l-2 border-orange-600/40 pl-6 mb-12">

  <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
    {subtitle}
  </p>

  {/* REGISTRATION & STRUCTURE CLARITY */}
  {/* CORPORATE STRUCTURE & REGISTRATION */}
<div className="mt-8 flex flex-col space-y-4 border-t border-white/10 pt-8">
  <p className="text-[12px] text-orange-500 font-black uppercase tracking-[0.3em] mb-2">
    Registered  Entities
  </p>

{/* WRAPPER - Stacks on mobile, side-by-side on large screens */}
<div className="flex flex-col lg:flex-row flex-wrap gap-6 w-full">
  
  {/* Division 1: Focus Inspection Technology */}
  <a 
    href="https://focusinspectiontechnology.com" 
    target="_blank" 
    rel="noopener noreferrer"
    className="flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 group transition-all p-4 rounded-xl hover:bg-white/5 w-full lg:w-auto shrink-0"
  >
    {/* LOGO CONTAINER */}
    <div className="w-full sm:w-32 h-32 sm:h-24 bg-white rounded-lg flex items-center justify-center shadow-2xl overflow-hidden group-hover:scale-105 transition-transform border border-white/10 shrink-0">
      <img 
        src={FIT}
        alt="Focus Inspection Technology Logo" 
        className="w-full h-full object-contain p-2"
        style={{ transform: 'scale(1.8)' }} 
      />
    </div>

    {/* TEXT AREA */}
    <div className="flex flex-col text-center sm:text-left min-w-max">
      <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
        <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] leading-none">
          Registered Entity
        </span>
        <div className="h-[1px] w-8 bg-orange-600/30"></div>
      </div>
      
      {/* whitespace-nowrap prevents the name from breaking into two lines */}
      <span className="text-xl sm:text-2xl lg:text-3xl font-black text-white group-hover:text-orange-500 transition-colors leading-none tracking-tight whitespace-nowrap">
        Focus Inspection <span className="text-orange-600 group-hover:text-white transition-colors">Technology</span>
      </span>

      <div className="hidden sm:block mt-4 h-[2px] w-0 bg-orange-600 group-hover:w-full transition-all duration-700 ease-in-out"></div>
    </div>
  </a>

  {/* Division 2: Focus NDT Innovations */}
  <a 
    href="#innovations" 
    className="flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 group transition-all p-4 rounded-xl hover:bg-white/5 w-full lg:w-auto shrink-0"
  >
    {/* LOGO CONTAINER */}
    <div className="w-full sm:w-32 h-32 sm:h-24 bg-white border border-orange-600/20 rounded-lg flex items-center justify-center shadow-2xl overflow-hidden group-hover:border-orange-500 group-hover:scale-105 transition-all shrink-0">
      <img 
        src={FNI} 
        alt="Focus NDT Innovations Logo" 
        className="w-full h-full object-contain p-4 transition-transform duration-500"
        style={{ transform: 'scale(2.1)' }} 
      />
    </div>

    {/* TEXT AREA */}
    <div className="flex flex-col text-center sm:text-left min-w-max">
      <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] leading-none">
          Registered Entity
        </span>
        <div className="h-[1px] w-8 bg-slate-700"></div>
      </div>

      <span className="text-xl sm:text-2xl lg:text-3xl font-black text-white group-hover:text-orange-500 transition-colors leading-none tracking-tight whitespace-nowrap">
        Focus NDT <span className="text-slate-400 group-hover:text-white transition-colors">Innovations</span>
      </span>

      <div className="hidden sm:block mt-4 h-[2px] w-0 bg-slate-500 group-hover:w-full transition-all duration-700 ease-in-out"></div>
    </div>
  </a>
</div>

  <p className="text-xs text-slate-500 italic leading-relaxed max-w-lg mt-2">
    The <span className="text-slate-300 font-semibold">Focus NDT Group</span> ecosystem serves as the parent domain unifying our specialized field inspection and digital technology divisions.
  </p>
</div>

  <p className="mt-8 text-slate-400 text-xs uppercase tracking-widest">
    NDT • Inspection • PAUT • TOFD • Offshore • Pipeline • Welding • Asset Integrity
  </p>

</div>



          {/* CTA */}

  <div className="flex flex-wrap gap-4">

  <a
    href="#ecosystem"
    className="group bg-orange-600 text-white px-10 py-4 font-bold rounded shadow-xl hover:bg-white hover:text-black transition text-xs uppercase tracking-widest flex items-center"
  >
    {primaryBtnText}
    <ArrowUpRight className="ml-2 w-4 h-4" />
  </a>

  <a
    href="#contact"
    className="px-10 py-4 font-bold rounded border border-white/20 hover:bg-white/10 transition text-xs uppercase tracking-widest"
  >
    {secondaryBtnText}
  </a>

</div>



          {/* TRUST BAR */}

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8 max-w-4xl">

            <div>
              <span className="flex items-center text-orange-500 font-black text-xl">
                <Globe className="w-4 h-4 mr-2" />
                Global
              </span>

              <span className="text-[10px] text-slate-500 uppercase">
                Offshore & International
              </span>
            </div>


            <div>
              <span className="flex items-center text-white font-black text-xl">
                <ShieldCheck className="w-4 h-4 mr-2 text-orange-500" />
                ISO
              </span>

              <span className="text-[10px] text-slate-500 uppercase">
                9712 Certified
              </span>
            </div>


            <div>
              <span className="flex items-center text-white font-black text-xl">
                <Activity className="w-4 h-4 mr-2 text-orange-500" />
                NDT 4.0
              </span>

              <span className="text-[10px] text-slate-500 uppercase">
                Digital Inspection
              </span>
            </div>


            <div>
              <span className="flex items-center text-white font-black text-xl">
                24/7
              </span>

              <span className="text-[10px] text-slate-500 uppercase">
                Field Support
              </span>
            </div>

          </div>

        </div>
      </div>



      {/* Big Background Text */}

      <div className="absolute right-0 bottom-0 opacity-10 hidden xl:block">

        <h2 className="text-[280px] font-black -mr-20 -mb-20 text-white select-none">
          FOCUS
        </h2>

      </div>

    </section>
  );
};

export default IndustrialHero;